import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getProgramTemplates } from "generated/api/program-templates";
import type {
  CampaignTouchpointSummaryDTOType,
  GetApprovedProgramTemplatesType,
  TouchpointCreateRequestType,
} from "generated/schemas";

import { programTemplateKeys } from "hooks/api/query-keys";

import type { OptionType } from "ui/select";

interface UseProgramTemplatesOptions {
  clientId: string | undefined;
  touchpointType: CampaignTouchpointSummaryDTOType | TouchpointCreateRequestType;
  /** When false (e.g. modal closed), templates are not fetched. */
  enabled?: boolean;
}

export const useProgramTemplatesOptions = ({
  clientId,
  touchpointType,
  enabled = true,
}: UseProgramTemplatesOptions) => {
  const { data: templates = [], isLoading } = useQuery({
    queryKey: programTemplateKeys.approved(clientId ?? "", touchpointType),
    queryFn: () =>
      getProgramTemplates().getApprovedProgramTemplates(
        { clientId: clientId! },
        { type: touchpointType as GetApprovedProgramTemplatesType },
      ),
    enabled: Boolean(clientId) && enabled,
  });

  const templateSelectOptions = useMemo<OptionType[]>(
    () =>
      templates.map((template) => ({
        value: template.touchpointId,
        label: template.touchpointName,
      })),
    [templates],
  );

  return { templates, isLoading, templateSelectOptions };
};
